import * as React from 'react';
import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import {Button, Form, Grid, Input, Space, Typography} from 'antd';
import {AuthFormContainer} from 'components/AuthFormContainer';
import {getIsMobile} from 'helpers/utils';
import {observer} from 'mobx-react-lite';
import {appState} from 'store/AppStore';
import {ResetPasswordRequest} from '../../../../../PRMGApiClient';

type ResetPasswordFiels = ResetPasswordRequest & {confirmPassword?: string};

export const ResetPasswordView: React.FC = observer((): JSX.Element => {
  const {t} = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();

  const screen = Grid.useBreakpoint();

  const isMobile = React.useMemo(() => getIsMobile(screen), [screen]);

  const onSubmit = React.useCallback((payload: ResetPasswordFiels) => {
    delete payload.confirmPassword;
    appState.resetPassword(payload);
  }, []);

  useEffect(() => {
    const token = searchParams.get('token');
    const email = searchParams.get('email');
    if (token && email) {
      searchParams.delete('token');
      searchParams.delete('email');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, form]);

  return (
    <AuthFormContainer>
      <Space direction="vertical">
        <Typography.Title level={isMobile ? 2 : 1}>{t('Reset password')}</Typography.Title>
        <Form form={form} onFinish={onSubmit} layout="vertical">
          <Form.Item hidden name="token" initialValue={searchParams.get('token')}>
            <Input />
          </Form.Item>
          <Form.Item required label={t('Email')} name="email" initialValue={searchParams.get('email')}>
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="password"
            required
            label={t('Password')}
            rules={[
              {required: true, message: String(t('Password is required.'))},
              {min: 8, message: String(t('Password must be at least 8 characters'))},
            ]}
          >
            <Input.Password type="password" placeholder={t('Password')} />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label={t('Confirm password')}
            dependencies={['password']}
            rules={[
              {
                required: true,
                message: String(t('Please confirm your password.')),
              },
              ({getFieldValue}) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('Passwords do not match!')));
                },
              }),
            ]}
          >
            <Input.Password placeholder={t('Confirm password')} />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>
            {String(t('Reset'))}
          </Button>
        </Form>
      </Space>
    </AuthFormContainer>
  );
});
